<template>
  <div class="col-xs-12">
    <div class="row q-col-gutter-sm">
      <!-- Add filter dropdown button -->
      <div>
        <q-btn-dropdown
          no-caps
          color="grey-3"
          text-color="grey-7"
          :label="filterLabel('addFilter')"
          ref="newFilterRef"
        >
          <div class="column q-pa-sm q-col-gutter-sm w-72">
            <!-- Inputs -->
            <filters-stackable-inputs
              :fields-as-array="fieldsAsArray"
              :fields="fields"
              @conditional-change="setNewCondition($event)"
              @enter-key="
                conditionReady(newCondition)
                  ? [
                      addCondition(newCondition),
                      resetNewCondition(),
                      conditionStackChange(),
                      closeDialog(newFilterRef),
                    ]
                  : ''
              "
            ></filters-stackable-inputs>
            <!-- Close/Apply filter -->
            <div align="right">
              <q-btn
                flat
                no-caps
                class="text-grey-7"
                :label="t(`form.cancel`)"
                @click.stop="resetNewCondition(), conditionStackChange()"
                v-close-popup
              />
              <q-btn
                flat
                no-caps
                class="text-primary"
                :label="t('data.filtering.applyFilter')"
                @click.stop="
                  conditionReady(newCondition)
                    ? [addCondition(newCondition), resetNewCondition(), conditionStackChange()]
                    : ''
                "
                v-close-popup
              />
            </div>
          </div>
        </q-btn-dropdown>
      </div>
      <template v-for="(condition, index) in processedConditionStack" :key="index">
        <!-- Edit filters dropdown buttons -->
        <div>
          <q-btn-dropdown
            color="grey-3"
            text-color="grey-7"
            no-caps
            @click.stop="editCondition = condition"
            ref="editFilterRef"
          >
            <!-- Label -->
            <template #label>
              <div class="row items-center no-wrap text-grey-10 text-weight-regular">
                <div>
                  <!-- Field -->
                  {{ conditionLabel(condition.field) }}
                  <!-- Operation -->
                  <span class="text-weight-regular text-grey-7">
                    {{ ' ' + operatorLabel(condition.operation) }}
                  </span>
                  <!-- Value -->
                  <span
                    v-if="
                      'dateModifier' in condition &&
                      condition.dateModifier !== null &&
                      condition.dateModifier?.key === 'exactDate'
                    "
                    :class="
                      condition.dateModifier !== null ? 'text-weight-regular text-grey-7' : ''
                    "
                  >
                    {{ ' ' + t(`data.filtering.operators.date.${condition.dateModifier?.key}`) }}
                  </span>
                  <span
                    v-if="
                      'dateModifier' in condition &&
                      condition.dateModifier !== null &&
                      condition.dateModifier?.key !== 'exactDate'
                    "
                  >
                    {{ ' ' + t(`data.filtering.operators.date.${condition.dateModifier?.key}`) }}
                  </span>
                  <span v-else-if="condition.field?.type === 'boolean'"> {{ ' ' }}</span>
                  <span v-else> {{ ' ' + condition.value }}</span>
                </div>
              </div>
            </template>
            <!-- Inputs -->
            <filters-stackable-inputs
              :fields-as-array="fieldsAsArray"
              :fields="fields"
              :inputs="condition"
              @conditional-change="updateEditCondition($event)"
              @enter-key="
                conditionReady(condition) && updateReady(condition)
                  ? [
                      updateCondition(index),
                      resetEditCondition(),
                      conditionStackChange(),
                      closeDialog(editFilterRef),
                    ]
                  : ''
              "
            ></filters-stackable-inputs>
            <div align="right">
              <q-btn
                flat
                no-caps
                class="text-grey-7"
                :label="t('form.cancel')"
                @click.stop="resetEditCondition()"
                v-close-popup
              />
              <q-btn
                flat
                no-caps
                class="text-negative"
                :label="t('form.deleteFilter')"
                @click.stop="removeCondition(index), conditionStackChange()"
                v-show="!updateReady(condition)"
                v-close-popup
              />
              <q-btn
                flat
                no-caps
                class="text-primary"
                :label="t('form.updateFilter')"
                v-show="updateReady(condition)"
                @click.stop="updateCondition(index), resetEditCondition(), conditionStackChange()"
                v-close-popup
              />
            </div>
          </q-btn-dropdown>
        </div>
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { LanguageChoice } from '../i18n'
import { computed, defineComponent, onBeforeMount, PropType, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isEqual } from 'lodash-es'
import { Condition, Fields, useConditionals } from '../composables/conditionals'
import FiltersStackableInputs from './FiltersStackableInputs.vue'
import { ColumnsMeta } from 'app/types/components/data-rendering'
import { QBtnDropdown } from 'quasar'
export default defineComponent({
  emits: ['conditionStack', 'closeConditionStack', 'conditionCount'],
  components: { FiltersStackableInputs },
  props: {
    fields: {
      required: true,
      type: Object as PropType<ColumnsMeta>,
    },
    stackLimit: {
      required: false,
      type: Number,
      default: null,
    },
    defaultConditions: {
      requried: false,
      type: Array as PropType<Condition[]>,
      default: null,
    },
  },
  setup(props, { emit, expose }) {
    const { locale, t } = useI18n({ useScope: 'global' })
    const localeString = computed(() => locale.value as LanguageChoice)
    const { addCondition, conditionStack, removeCondition } = useConditionals()

    onBeforeMount(() => {
      //set default conditions
      if (props.defaultConditions && props.defaultConditions.length > 0) {
        props.defaultConditions.forEach((condition) => {
          addCondition(condition)
        })
        conditionStackChange()
      }
    })
    const addConditionAndEmit = (condition: Condition) => {
      addCondition(condition)
      conditionStackChange()
    }
    expose({ addCondition: addConditionAndEmit })
    /*
     * Logic for fields
     */
    const fieldsAsArray = computed((): Fields => {
      const out: any = []
      for (const name in props.fields) {
        const val = props.fields[name]
        // if (!val.hideInFilters) {
        out.push({ name: name, label: val.label, type: val.type })
        // }
      }
      return out
    })

    /*
     * Logic for new and editing conditions
     */
    const newCondition: Ref<Condition> = ref({ field: null, operation: null, value: null })
    const setNewCondition = (event: Condition) => {
      newCondition.value = event
    }
    const resetNewCondition = () => {
      newCondition.value = { field: null, operation: null, value: null }
    }
    const editCondition: Ref<any> = ref({
      field: null,
      operation: null,
      value: null,
      dateModifier: null,
    })
    const updateEditCondition = (event: Condition) => {
      editCondition.value = event
    }
    const resetEditCondition = () => {
      editCondition.value = { field: null, operation: null, value: null, dateModifier: null }
    }
    const updateCondition = (index: number) => {
      //for 'is empty', null value needed
      if (editCondition.value.operation.operation.length < 2) {
        editCondition.value.value = null
      }
      conditionStack.value[index] = editCondition.value
    }
    const closeConditionStack = (): void => {
      if (conditionStack.value.length < 1) {
        emit('closeConditionStack')
      }
    }
    const updateReady = (condition: Condition): boolean => {
      if (
        !isEqual(condition, editCondition.value) &&
        editCondition.value.field !== null &&
        editCondition.value.operation !== null &&
        (editCondition.value.value !== null ||
          editCondition.value.operation?.operatesOn === 'boolean')
      ) {
        return true
      } else {
        return false
      }
    }
    const processedConditionStack = computed(() => {
      const conditionStackProcessed: Condition[] = []
      if (conditionStack.value.length === 0) return conditionStackProcessed
      for (const condition of conditionStack.value) {
        let date: string | number | null = null
        if (getDate(condition) != null) {
          date = getDate(condition)
        }
        conditionStackProcessed.push({
          field: condition.field ? condition.field : null,
          operation: condition.operation ? condition.operation : null,
          dateModifier: condition.dateModifier ? condition.dateModifier : null,
          value: condition.value ? condition.value : date,
          options: condition.options ? condition.options : undefined,
          optionsKey: condition.optionsKey ? condition.optionsKey : undefined,
        })
      }
      return conditionStackProcessed
    })
    const conditionStackChange = (): void => {
      emit('conditionStack', processedConditionStack.value)
      emit('conditionCount', conditionCount.value)
    }

    const getDate = (condition: Condition) => {
      if (
        condition !== null &&
        typeof condition.dateModifier !== 'undefined' &&
        'dateModifier' in condition &&
        condition.dateModifier !== null
      ) {
        return condition.dateModifier.date()
      } else {
        return null
      }
    }
    const filterLabel = (field: string) => {
      return t(`data.filtering.${field}`)
    }
    const conditionLabel = (field: Condition['field']): string => {
      return field ? field.label[localeString.value] : ''
    }
    const operatorLabel = (operation: Condition['operation']): string => {
      return operation ? t(`data.filtering.operators.comparison.${operation.key}`) : ''
    }
    //number of completely filled conditions
    const conditionCount = computed(() => {
      return conditionStack.value.filter((condition) => {
        if (
          condition.field != null &&
          condition.operation != null &&
          (condition.value != null || condition.operation.operation.length < 2)
        ) {
          return true
        }
      }).length
    })
    const newFilterRef: Ref<QBtnDropdown | null> = ref(null)
    const editFilterRef: Ref<QBtnDropdown | null> = ref(null)
    const conditionReady = (condition: Condition): boolean => {
      if (
        condition.field !== null &&
        condition.operation !== null &&
        (condition.value !== null ||
          condition.operation.operatesOn == 'boolean' ||
          condition.dateModifier !== null)
      ) {
        return true
      }
      return false
    }
    const closeDialog = (filterRef: QBtnDropdown | null) => {
      if (filterRef) {
        filterRef.hide()
      }
    }
    return {
      newCondition,
      addCondition,
      removeCondition,
      conditionStack,
      conditionLabel,
      operatorLabel,
      conditionCount,
      setNewCondition,
      resetNewCondition,
      editCondition,
      updateEditCondition,
      resetEditCondition,
      updateCondition,
      conditionStackChange,
      closeConditionStack,
      updateReady,
      processedConditionStack,
      fieldsAsArray,
      getDate,
      localeString,
      newFilterRef,
      editFilterRef,
      conditionReady,
      closeDialog,
      t,
      filterLabel,
    }
  },
})
</script>
